/**
 * LearningCenterDetailPage (/partners/learning-centers/:slug) — one Learning
 * Center Partner's full profile, driven entirely from src/data/partners.ts.
 * Every paragraph is transcribed from the centre's dedicated page on the old
 * IBAS site — nothing is invented — and ECC Knowledge Group's copy is
 * contractually VERBATIM-LOCKED (rendered exactly as supplied, never edited
 * here). Conditional blocks (collaboration wording, gallery, addresses,
 * websites, logos) render only where the source page carries them, so a
 * centre without photos still resolves to a clean, text-only page. Unknown
 * slug → back to /partners (never an empty/fabricated page).
 */
import { ArrowLeft, ArrowUpRight, Check, Globe, MapPin } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { brand } from '@/config/brand';
import {
  getLearningCenterPartner,
  LEARNING_CENTER_COLLAB_HEADING,
  LEARNING_CENTER_PARTNERS,
  type LearningCenterSection,
} from '@/data/partners';
import {
  Magnetic,
  MediaFrame,
  NavyAtmosphere,
  ScrollReveal,
  SplitReveal,
  TextStagger,
} from '@/components/motion';
import { Button } from '@ibas/ui';

/**
 * One profile block: optional heading, then any of paragraphs / bullet list /
 * label→detail facts grid — mirroring how the source pages structure their
 * copy (intro → vision/mission/values → offices).
 */
function ProfileSection({ section }: { section: LearningCenterSection }) {
  return (
    <div>
      {section.heading && (
        <SplitReveal
          as="h2"
          by="lines"
          className="font-display text-2xl leading-tight text-foreground md:text-3xl"
        >
          {section.heading}
        </SplitReveal>
      )}

      {section.paragraphs && (
        <ScrollReveal from="up" stagger={0.06} className="mt-4 flex flex-col gap-4">
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className="text-base leading-relaxed text-muted-foreground">
              {paragraph}
            </p>
          ))}
        </ScrollReveal>
      )}

      {section.list && (
        <ScrollReveal from="up" stagger={0.04} as="ul" className="mt-4 flex flex-col gap-2.5">
          {section.list.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 text-base leading-relaxed text-muted-foreground"
            >
              <span aria-hidden className="mt-2.5 size-1.5 shrink-0 rounded-full bg-brand-gold" />
              {item}
            </li>
          ))}
        </ScrollReveal>
      )}

      {section.facts && (
        <ScrollReveal
          from="up"
          stagger={0.06}
          className="mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2"
        >
          {section.facts.map((fact) => (
            <div key={fact.label} className="bg-background p-5 md:p-6">
              <p className="font-label text-xs tracking-[0.16em] text-brand-gold uppercase">
                {fact.label}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground">{fact.detail}</p>
            </div>
          ))}
        </ScrollReveal>
      )}
    </div>
  );
}

export function LearningCenterDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const centre = getLearningCenterPartner(slug);

  // Unknown slug → back to the partners page (never an empty page).
  if (!centre) {
    return <Navigate to="/partners" replace />;
  }

  const others = LEARNING_CENTER_PARTNERS.filter((p) => p.slug !== centre.slug);
  // Short display name for headings — drops a bracketed alias, e.g.
  // "Imperial Institute of Higher Education (IIHE)" → the part before " (".
  const shortName = centre.name.split(' (')[0] ?? centre.name;

  return (
    <div className="bg-background">
      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section className="relative isolate overflow-hidden bg-brand-navy">
        <NavyAtmosphere />
        <div className="relative z-10 mx-auto w-full max-w-6xl px-6 pt-28 pb-16 md:pt-36 md:pb-20">
          <Link
            to="/partners#learning-center-partners"
            className="group inline-flex items-center gap-2 font-label text-xs tracking-[0.18em] text-white/70 uppercase transition-colors hover:text-white"
          >
            <ArrowLeft
              aria-hidden
              className="size-4 transition-transform duration-300 group-hover:-translate-x-0.5 motion-reduce:transition-none"
            />
            All Learning Center Partners
          </Link>

          <div className="mt-8 max-w-3xl">
            <TextStagger
              text="Learning Center Partner"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.08] text-white md:text-5xl lg:text-6xl"
            >
              {centre.name}
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-5 flex items-center gap-2 text-base text-white/80 md:text-lg">
                <MapPin aria-hidden className="size-5 shrink-0 text-brand-gold-light" />
                {centre.country}
              </p>
            </ScrollReveal>
          </div>

          {/* Logos + external site links — only where the source carries them. */}
          {(centre.logos || centre.websites) && (
            <ScrollReveal from="up" delay={0.2} className="mt-10 flex flex-wrap items-center gap-4">
              {centre.logos?.map((logo) => (
                <span key={logo.src} className="inline-flex rounded-xl bg-white p-3">
                  <img src={logo.src} alt={logo.alt} loading="lazy" className="h-14 w-auto" />
                </span>
              ))}
              {centre.websites?.map((site) => (
                <a
                  key={site.href}
                  href={site.href}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-2 text-sm font-medium text-white/85 transition-colors hover:border-brand-gold/50 hover:text-white"
                >
                  <Globe aria-hidden className="size-4 text-brand-gold-light" />
                  {site.label}
                  <ArrowUpRight
                    aria-hidden
                    className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transition-none"
                  />
                </a>
              ))}
            </ScrollReveal>
          )}
        </div>
      </section>

      {/* ── PROFILE ───────────────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-12 px-6 md:gap-14">
          {centre.sections.map((section, index) => (
            <ProfileSection key={`${section.heading ?? 'intro'}-${index}`} section={section} />
          ))}
        </div>
      </section>

      {/* ── IN COLLABORATION WITH IBAS (conditional) ───────────────────────── */}
      {centre.collaboration && (
        <section className="border-t border-border bg-muted/40 py-16 md:py-24">
          <div className="mx-auto w-full max-w-3xl px-6">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              The partnership
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-2xl leading-tight text-foreground md:text-3xl"
            >
              {LEARNING_CENTER_COLLAB_HEADING}
            </SplitReveal>

            <ScrollReveal from="up" stagger={0.06} className="mt-5 flex flex-col gap-4">
              {centre.collaboration.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-base leading-relaxed text-muted-foreground">
                  {paragraph}
                </p>
              ))}
            </ScrollReveal>

            {centre.collaboration.programmes && (
              <ScrollReveal from="up" stagger={0.06} as="ul" className="mt-7 flex flex-col gap-3">
                {centre.collaboration.programmes.map((programme) => (
                  <li
                    key={programme}
                    className="flex items-start gap-4 rounded-xl border border-border bg-background p-5"
                  >
                    <span
                      aria-hidden
                      className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-navy text-white"
                    >
                      <Check className="size-4" />
                    </span>
                    <p className="text-base leading-relaxed text-foreground">{programme}</p>
                  </li>
                ))}
              </ScrollReveal>
            )}

            {centre.collaboration.after && (
              <ScrollReveal from="up" stagger={0.06} className="mt-7 flex flex-col gap-4">
                {centre.collaboration.after.map((paragraph) => (
                  <p key={paragraph} className="text-base leading-relaxed text-muted-foreground">
                    {paragraph}
                  </p>
                ))}
              </ScrollReveal>
            )}
          </div>
        </section>
      )}

      {/* ── GALLERY (conditional) ─────────────────────────────────────────── */}
      {centre.gallery && centre.gallery.length > 0 && (
        <section className="bg-background py-16 md:py-24">
          <div className="mx-auto w-full max-w-6xl px-6">
            <header className="mb-10 max-w-2xl">
              <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
                In pictures
              </p>
              <SplitReveal
                as="h2"
                by="lines"
                className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
              >
                {`${shortName}, on the ground.`}
              </SplitReveal>
            </header>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {centre.gallery.map((item) => (
                <MediaFrame
                  key={item.src}
                  image={item.src}
                  alt={item.alt}
                  aspect="aspect-[4/3]"
                  rounded="rounded-2xl"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── LOCATION DETAILS (conditional) ─────────────────────────────────── */}
      {centre.locations && (
        <section className="border-t border-border bg-muted/30 py-16 md:py-24">
          <div className="mx-auto w-full max-w-3xl px-6">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Location details
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-2xl leading-tight text-foreground md:text-3xl"
            >
              {`Where to find ${shortName}.`}
            </SplitReveal>

            <ScrollReveal from="up" stagger={0.06} as="ul" className="mt-7 flex flex-col gap-4">
              {centre.locations.map((location) => (
                <li
                  key={location}
                  className="flex items-start gap-4 rounded-xl border border-border bg-background p-5"
                >
                  <MapPin aria-hidden className="mt-0.5 size-5 shrink-0 text-brand-gold" />
                  <p className="text-base leading-relaxed text-foreground">{location}</p>
                </li>
              ))}
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ── OTHER LEARNING CENTERS ────────────────────────────────────────── */}
      <section className="border-t border-border bg-background py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <header className="mb-8 max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Keep exploring
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              Other Learning Center Partners.
            </SplitReveal>
          </header>

          <ScrollReveal
            from="up"
            stagger={0.05}
            as="ul"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {others.map((other) => (
              <li key={other.slug}>
                <Link
                  to={`/partners/learning-centers/${other.slug}`}
                  className="group flex h-full items-center justify-between gap-4 rounded-xl border border-border bg-muted/30 p-5 transition-colors duration-300 hover:border-brand-gold/40 hover:bg-muted/60 motion-reduce:transition-none"
                >
                  <div className="min-w-0">
                    <p className="font-display text-base leading-tight font-semibold text-foreground md:text-lg">
                      {other.name}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">{other.country}</p>
                  </div>
                  <ArrowUpRight
                    aria-hidden
                    className="size-5 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-gold motion-reduce:transition-none"
                  />
                </Link>
              </li>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── CLOSING CTA ───────────────────────────────────────────────────── */}
      <section className="relative isolate overflow-hidden bg-brand-navy py-20 md:py-28">
        <NavyAtmosphere />
        <div className="relative z-10 mx-auto w-full max-w-3xl px-6 text-center">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold-light uppercase">
            Take the next step
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-white md:text-5xl"
          >
            {brand.supportingPhrase}
          </SplitReveal>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/80">
            Interested in studying through a Learning Center Partner? Start your
            application, or talk to an adviser about the right route for you.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Magnetic>
              <Button asChild size="lg" variant="secondary">
                <Link to="/apply">Start your application</Link>
              </Button>
            </Magnetic>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/admissions#find-advisor">Connect with an adviser</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
